/**
 * Authentication Service
 *
 * Singleton service for managing authentication state and operations using MSAL Browser.
 * Handles login, logout, token acquisition, and authentication status.
 */

import { PublicClientApplication, InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './config';

class AuthService {
	private msalInstance: PublicClientApplication;
	private initialized = false;
	private pendingReauthentication: Promise<void> | null = null;

	constructor() {
		this.msalInstance = new PublicClientApplication(msalConfig);
	}

	/**
	 * Initialize MSAL and handle redirect response
	 * Call this once on app startup
	 */
	async initialize(): Promise<void> {
		if (this.initialized) {
			return;
		}

		await this.msalInstance.initialize();

		try {
			// Handle redirect response if returning from Azure AD login
			// This will throw if there's no redirect to handle (normal case for direct navigation)
			await this.msalInstance.handleRedirectPromise();
		} catch (error) {
			// Ignore "no_token_request_cache_error" - this is expected when not returning from a redirect
			if (error instanceof Error && error.message.includes('no_token_request_cache_error')) {
				// Expected when user navigates directly to the app
			} else {
				console.error('Error handling redirect:', error);
			}
		}

		this.initialized = true;
	}

	/**
	 * Initiate login flow (redirect-based)
	 */
	async login(): Promise<void> {
		try {
			await this.msalInstance.loginRedirect(loginRequest);
		} catch (error) {
			console.error('Login failed:', error);
			throw error;
		}
	}

	/**
	 * Logout and redirect — user-initiated explicit logout only.
	 * Do NOT call this from the 401 response interceptor; use reauthenticate() instead.
	 */
	async logout(): Promise<void> {
		await this.msalInstance.logoutRedirect();
	}

	/**
	 * Get access token for API calls
	 * Attempts silent acquisition, falls back to interactive if needed
	 */
	async getAccessToken(): Promise<string | null> {
		const accounts = this.msalInstance.getAllAccounts();
		if (accounts.length === 0) return null;

		try {
			const response = await this.msalInstance.acquireTokenSilent({
				...loginRequest,
				account: accounts[0],
			});
			return response.accessToken;
		} catch (error) {
			if (error instanceof InteractionRequiredAuthError) {
				await this.msalInstance.acquireTokenRedirect(loginRequest);
			} else {
				// monitor_window_timeout and similar: silent refresh failed, no usable token.
				// Return null and let the 401 response interceptor drive recovery via reauthenticate(),
				// which dedupes concurrent calls.
				console.warn('Silent token acquisition failed:', error);
			}
			return null;
		}
	}

	/**
	 * Force a silent token refresh, bypassing any cached access token.
	 * Returns the new token, or null on failure. Does NOT fall back to interactive auth —
	 * the 401 interceptor decides what to do next.
	 */
	async forceRefreshAccessToken(): Promise<string | null> {
		const accounts = this.msalInstance.getAllAccounts();
		if (accounts.length === 0) return null;

		try {
			const response = await this.msalInstance.acquireTokenSilent({
				...loginRequest,
				account: accounts[0],
				forceRefresh: true,
			});
			return response.accessToken;
		} catch (error) {
			console.error('Forced token refresh failed:', error);
			return null;
		}
	}

	/**
	 * Clear the local MSAL cache and re-enter the login flow.
	 * Used when a 401 cannot be silently recovered — re-login usually completes
	 * silently via SSO if the Microsoft session cookie is still valid, avoiding
	 * the "You signed out of your account" screen that logoutRedirect produces.
	 *
	 * Single-flight: concurrent 401s on a stale tab all converge on one redirect
	 * instead of racing multiple clearCache/loginRedirect calls.
	 */
	async reauthenticate(): Promise<void> {
		if (!this.pendingReauthentication) {
			this.pendingReauthentication = (async () => {
				try {
					await this.msalInstance.clearCache();
				} catch (error) {
					console.error('Failed to clear MSAL cache before re-login:', error);
				}
				await this.msalInstance.loginRedirect(loginRequest);
			})().finally(() => {
				// Clear on settle so a rejected loginRedirect (popup blocked, interaction_in_progress,
				// MSAL network failure) doesn't permanently wedge reauth for the tab's lifetime. In the
				// happy path the page navigates away before this runs, so clearing is a no-op.
				this.pendingReauthentication = null;
			});
		}
		return this.pendingReauthentication;
	}

	/**
	 * Check if user is authenticated
	 */
	async isAuthenticated(): Promise<boolean> {
		const accounts = this.msalInstance.getAllAccounts();
		return accounts.length > 0;
	}

	/**
	 * Get current user account info
	 */
	getCurrentUser(): AccountInfo | null {
		const accounts = this.msalInstance.getAllAccounts();
		return accounts[0] || null;
	}

	/**
	 * Get the tenant ID of the current user
	 */
	getTenantId(): string | null {
		return this.getCurrentUser()?.tenantId ?? null;
	}
}

// Export singleton instance
export const authService = new AuthService();
